import { useCallback } from 'react';

import { KeyRoundIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { FilterToolbar } from '@/components/organisms/FilterToolbar/FilterToolbar';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import useMembers from '@/hooks/useMembers';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import { useSidebar } from '@/hooks/useSidebar';
import { getPermissions } from '@/redux/permissions/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { API_KEYS_DOCS_URL } from '@/util/constants';

import { APIKeyItem } from './APIKeyItem';
import { EmptyAPIKeys } from './EmptyAPIKeys';
import { apiKeyTypeLabelMapper } from './helper';
import {
  CREATOR_FILTER,
  creatorFilterDefinition,
} from '../../components/ResourceList/filterDefinitions';
import { ResourceList } from '../../components/ResourceList/ResourceList';
import useCredentials from '../../hooks/useCredentials';
import { byLastUsed, useResourceList } from '../../hooks/useResourceList';
import { Credential, CredentialType, DEV_TOOLS_SIDEBAR_KEYS } from '../../types';

const TYPE_FILTER = 'type';
const CREDENTIAL_TYPES: CredentialType[] = ['manage', 'use', 'analytics'];

interface APIKeysProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
}

export const APIKeys = ({ onEmptyStateChange }: APIKeysProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { credentials, listStatus } = useCredentials();
  const { getMember } = useMembers();
  const { visibleItems, toolbarProps } = useResourceList(credentials, {
    storageKey: 'apiKeysFilters',
    preferencesKey: 'api_keys',
    filterValues: {
      [TYPE_FILTER]: (credential) => credential.type,
      [CREATOR_FILTER]: (credential) => credential.created_by,
    },
    searchText: (credential) => [credential.name, apiKeyTypeLabelMapper(credential.type, t)],
    sort: byLastUsed,
  });

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'credentials', actions.WRITE)
  );

  useTitle(t('pages.devtool_apikey'));

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'credentials',
    isLoading,
    isEmpty: credentials.length === 0,
    isError: listStatus === 'error',
  });

  useReportEmptyState(isEmptyPage, onEmptyStateChange);

  useSegments(PageName.API_KEYS);

  const handleGenerate = useCallback(() => {
    dispatch(setSidebarContent(DEV_TOOLS_SIDEBAR_KEYS.apikeys));
  }, [dispatch]);

  const { meta, isOpen, open } = useSidebar();
  const selectedCredentialId = isOpen('api_key_details') ? meta?.credentialId : undefined;

  const handleOpen = (credential: Credential) => {
    open('api_key_details', { credentialId: credential.credential_id });
  };

  const filterDefinitions = [
    {
      id: TYPE_FILTER,
      label: t('common.type'),
      icon: KeyRoundIcon,
      type: 'checkbox' as const,
      options: CREDENTIAL_TYPES.map((type) => ({
        value: type,
        label: apiKeyTypeLabelMapper(type, t),
      })),
    },
    creatorFilterDefinition(credentials, getMember, t),
  ];

  return (
    <div className="pt-5 pb-20">
      <ContextualHelp
        title={t('developer_tools.contextual_help.api_keys_title')}
        name="about_api_keys"
        links={[{ label: t('common.learn_more'), url: API_KEYS_DOCS_URL }]}
      >
        {t('developer_tools.contextual_help.api_keys_message')}
      </ContextualHelp>

      {showEmpty && <EmptyAPIKeys canWrite={canWrite} onGenerate={handleGenerate} />}

      {showEmptySkeleton && <EmptySkeleton descriptionLines={3} />}

      {showList && (
        <>
          <FilterToolbar
            {...toolbarProps}
            searchPlaceholder={t('developer_tools.api_keys_search')}
            filterDefinitions={filterDefinitions}
          />

          <ResourceList
            isLoading={isLoading}
            isEmpty={visibleItems.length === 0}
            noResultsMessage={t('developer_tools.api_keys_no_results')}
          >
            {visibleItems.map((credential) => (
              <APIKeyItem
                key={credential.credential_id}
                credential={credential}
                isSelected={credential.credential_id === selectedCredentialId}
                onOpen={handleOpen}
              />
            ))}
          </ResourceList>
        </>
      )}
    </div>
  );
};
